import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { DOCTORS } from "../lib/constants";

/**
 * Compact row of doctor cards shown above the homepage H1.
 * Below md: a single swipeable snap-row, so all four stay on one row.
 * md and up: a static 4-column grid.
 */
export default function DoctorsStrip() {
  return (
    <div className="animate-fade-in-up" data-testid="hero-doctors-strip">
      {/* Section label */}
      <div className="flex items-center gap-3">
        <span className="h-px w-8 bg-teal-600/80" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-700 sm:text-xs">
          Meet Our Specialists
        </span>
        <span className="hidden h-px flex-1 bg-gradient-to-r from-teal-200 to-transparent sm:block" />
        <Link
          to="/about"
          className="ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-slate-500 transition-colors hover:text-teal-700 sm:ml-0 sm:text-xs"
          data-testid="hero-doctors-strip-link"
        >
          View profiles <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Cards */}
      <div className="no-scrollbar -mx-4 mt-4 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-4 px-4 pb-1 sm:-mx-6 sm:scroll-px-6 sm:px-6 md:mx-0 md:grid md:grid-cols-4 md:gap-4 md:overflow-visible md:px-0 md:pb-0 lg:gap-5">
        {DOCTORS.map((d, i) => (
          <Link
            key={d.name}
            to="/about"
            aria-label={`${d.name} — ${d.specialty}`}
            data-testid={`hero-doctor-card-${i}`}
            className="group relative flex w-[44vw] max-w-[190px] flex-none snap-start flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_30px_-20px_rgba(15,23,42,0.35)] transition-all duration-300 hover:-translate-y-1 hover:border-teal-300 hover:shadow-[0_22px_45px_-22px_rgba(13,148,136,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 md:w-auto md:max-w-none"
          >
            {/* Photo */}
            <div className="relative h-32 overflow-hidden bg-gradient-to-b from-teal-50 via-teal-50/50 to-white sm:h-36 lg:h-44">
              <img
                src={d.image}
                alt={`${d.name} — ${d.specialty} at Lotus Family Dental, Allahabad`}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.07]"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white via-white/70 to-transparent" />
              <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white/95 shadow-sm ring-1 ring-teal-100">
                <BadgeCheck className="h-3.5 w-3.5 text-teal-600" />
              </span>
            </div>

            {/* Details */}
            <div className="flex flex-1 flex-col px-2.5 pb-4 pt-1 text-center sm:px-3">
              <h3 className="font-heading text-[13px] font-semibold leading-tight text-slate-900 sm:text-sm lg:text-base">
                {d.name}
              </h3>
              <p className="mt-1 text-[10px] font-medium leading-snug text-slate-500 sm:text-[11px]">
                {d.qualifications}
              </p>
              <p className="mt-auto pt-2 text-[9px] font-semibold uppercase leading-relaxed tracking-[0.1em] text-teal-700 sm:text-[10px]">
                {d.specialty}
              </p>
            </div>

            {/* Hover accent */}
            <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] origin-left scale-x-0 bg-gradient-to-r from-teal-500 to-teal-700 transition-transform duration-500 group-hover:scale-x-100" />
          </Link>
        ))}
      </div>
    </div>
  );
}
